/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { CustomerCheckout } from "@lemmonhq/types";
import { PendingOrderCard } from "./PendingOrderCard";
import { GroupMember } from "../../../providers/Menu/realtime/index";
import { getCharacterNameForCustomer } from "../../../pages/ShopGroupOrder/utils/packs";
import { useMenu } from "../../../providers";
import { useLocalization } from "@lemmonhq/uikit";

export interface PendingOrdersListProps {}

export const PendingCheckoutsList = defineComponent<PendingOrdersListProps>({
  name: "PendingOrdersList",
  props: [],
  setup(props) {
    const { groupOrder, realtime } = useMenu();
    const { t } = useLocalization();
    // Build safe list based on actual checkout data:
    // Show members who either:
    // 1. Have checkouts that aren't completed (status='new' or 'confirmed')
    // 2. OR have no checkouts at all (haven't submitted anything)
    const pendingMembers = computed(() => {
      const groupMembers = realtime.groupState.value.members as GroupMember[];
      return groupMembers.filter((member) => {
        const groupCheckouts = groupOrder.value?.checkouts || [];
        // No checkouts at all - they're pending
        if (!groupCheckouts || groupCheckouts.length === 0) {
          return true;
        }

        // Has at least one checkout that isn't completed - they're pending
        const hasNonCompletedCheckout = groupCheckouts.some(
          (checkout) => checkout.customer_id === member.clientId,
        );
        return !hasNonCompletedCheckout;
      });
    });

    return () => (
      <div class="flex flex-col flex-1">
        <div class="flex items-center gap-3 mb-6 hidden">
          <div class="px-4">
            <h2 class={`font-bold text-sm`}>
              {t("customer.checkout_status.pending_checkouts")}
            </h2>
            <p class="text-sm text-base-content/60">
              {t(
                pendingMembers.value.length === 1
                  ? "customer.checkout_status.member_still_ordering_one"
                  : "customer.checkout_status.member_still_ordering_many",
                [pendingMembers.value.length],
              )}
            </p>
          </div>
        </div>

        {pendingMembers.value.length === 0 ? (
          <div class="rounded flex justify-center items-center shadow-lg hover:shadow-xl bg-base-200 border rounded-lg min-h-full">
            <div class=" text-center py-2 ">
              <h3 class="text-lg font-semibold mb-2">
                {t("customer.checkout_status.all_submitted")}
              </h3>
              <div class="text-3xl mb-4 ">🎉</div>
            </div>
          </div>
        ) : (
          <div class="">
            {pendingMembers.value.map((member) => {
              // Get character name for this member
              const characterName = groupOrder.value
                ? getCharacterNameForCustomer(groupOrder.value, member.clientId)
                : null;

              return (
                <PendingOrderCard
                  key={member.clientId}
                  members={pendingMembers.value}
                  member={member}
                  characterName={characterName}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  },
});
